import { isEmpty, trim } from 'lodash';
import * as Yup from 'yup';

import { isValidPhoneNumber } from 'utils/countries-helpers';
import {
  EMAIL_FREE_PROVIDERS,
  EMAIL_SERVICE_NAMES,
} from 'containers/pages/Messaging/CampaignDialog/constants';

const nameLengthValidation = (value) => {
  const characterLength = value.length;
  if (characterLength < 2) {
    return {
      isValid: false,
      error: 'Team name must be at least 2 characters.',
    };
  }
  if (characterLength > 30) {
    return {
      isValid: false,
      error: 'Team name must be 30 characters or less.',
    };
  }
  return { isValid: true, error: '' };
};

const nameSpecialCharactersValidation = (value) => {
  const regrex = /^[\w\s\-.,@'^*():|~`<>!]+$/;

  if (regrex.test(value)) {
    return { isValid: true, error: '' };
  }

  return {
    isValid: false,
    error: "Invalid character, allowed characters are a-z0-9.,-_@'^*():|~`<>!",
  };
};

export const nameValidation = (value) => {
  const nameLength = nameLengthValidation(value);
  const specialCharacters = nameSpecialCharactersValidation(value);

  if (nameLength.isValid) {
    return specialCharacters;
  }

  return nameLength;
};

export const emailValidation = Yup.object().shape({
  value: Yup.string().email().required(),
});

export const required = (value) => {
  if (isEmpty(trim(value))) {
    return 'Required';
  }

  return undefined;
};

export const isValidEmail = (value) => {
  const emailRegex = /^[a-z0-9]+([.+_-]{1}[a-z0-9]+)*@[a-z0-9]+([.-]{1}[a-z0-9]+)*\.[a-z]{2,}$/i;
  const test = emailRegex.test(value);

  return test;
};

export function isTCRAprovedEmail(value) {
  if (!isValidEmail(value)) {
    return 'Please enter valid email.';
  }
  const [name, host] = value.split('@');
  if (EMAIL_SERVICE_NAMES.includes(name.toUpperCase())) {
    return `${name} is a distribution list. Please enter a different email address.`;
  }
  if (EMAIL_FREE_PROVIDERS.includes(host.toLowerCase())) {
    return `${host} is a free email provider. Please enter a business email address.`;
  }
  return true;
}

export const isValidVerifyDomainCode = (value) => {
  const codeRegex = /^\d{6,6}$/i;

  return codeRegex.test(value) ? undefined : 'Invalid Code';
};

// this function can be deleted when ManageUser.js is deleted
export const isValidPhone = (number, code = {}) => {
  if (number && (number.isNaN || !isValidPhoneNumber({ number, code }))) {
    return 'Invalid phone';
  }

  return false;
};

export const isValidCallerId = (value) => {
  const regex = /^[a-z0-9!#$&'()*+,.:;<=>?@[\] ^_`{|}~-]*$/i;
  const maxChars = 15;

  if (value.length > maxChars) {
    return `must be ${maxChars} characters or less`;
  }
  if (!regex.test(value)) {
    return 'must be ASCII printable';
  }

  return '';
};

export const maxLength = (max) => {
  return (value) => {
    if (!isEmpty(value) && value.length > max) {
      return `Must be ${max} characters or less`;
    }

    return undefined;
  };
};

export const validData = (max, value) => {
  const regex = /[^a-zA-Z0-9.,\-_@'/"^*():|~`<>!\s]/;

  if (!isEmpty(value) && value.length > max) {
    return `must be ${max} characters or less`;
  }
  if (isEmpty(value)) {
    return 'required';
  }
  if (regex.test(value)) {
    return 'cannot contain special characters';
  }

  return '';
};

export const validString = (value) => {
  const regex = /[^a-zA-Z0-9.,\-_@'"^*():|~`<>!\s]/;

  if (regex.test(value)) {
    return 'Allowed: a-z0-9.,-_@\'"^*():|~`<>!';
  }

  return undefined;
};

export const isAccountManager = (userObj, companyInfo) => {
  const {
    accountManagerUUID,
    account_manager: { contact } = {},
    accountManager: { contact: { emailAddress } = {}, accountManagerUuid } = {},
  } = companyInfo;

  if (userObj.nextiva_uuid) {
    return accountManagerUUID
      ? userObj.nextiva_uuid === accountManagerUUID
      : userObj.email === contact?.email_address;
  }

  return accountManagerUuid
    ? userObj.nextivaUuid === accountManagerUuid
    : userObj.email === emailAddress;
};

export const formatToTestId = (str) =>
  str.replace(/[()]/g, '').replace(/\s+/g, '-');

export function isValidUrl(value) {
  const urlRegex = /^(http|https):\/\/.+$/;
  // Prepend https:// if the URL does not have a http[s] protocol.
  const url = urlRegex.test(value) ? value : `https://${value}`;
  try {
    const urlObject = new URL(url);
    if (urlObject.protocol !== 'http:' && urlObject.protocol !== 'https:') {
      // Must be a valid URL with http or https protocol.
      return false;
    }
    if (urlObject.username || urlObject.password) {
      // Username and password are not allowed in the URL.
      return false;
    }
    const hostParts = urlObject.hostname.split('.');
    if (hostParts.length < 2) {
      // Must be a valid URL with a toplevel domain name.
      return false;
    }
    if (hostParts[hostParts.length - 1].length < 2) {
      // Top-level domain must be at least 2 characters.
      return false;
    }
    if (/\s/.test(url)) {
      // Must not contain whitespace in the url.
      return false;
    }
    return true;
  } catch (e) {
    // Could not parse URL. Invalid.
    return false;
  }
}
